// Shared utility function to generate payment metadata for any payment gateway
export interface PaymentMetadata {
  store_id: string;
  payment_type: 'paystack' | 'stripe' | 'monnify' | 'bank';
  timestamp: number;
  order_items?: {
    id: string | number;
    quantity: number;
    variations?: any[];
  }[];
  amounts?: {
    order: number;
    shipping: number;
    vat: number;
    total: number;
  };
  customer_info?: {
    email: string;
    name: string;
  };
  custom_fields?: { display_name: string; variable_name: string; value: string }[];
  transaction_type?: 'order' | 'subscription' | 'domain';
  subscription_info?: {
    period: number;
    type: 'monthly' | 'yearly';
    is_special_site: boolean;
  };
  domain_info?: {
    domain: string;
    original_email: string;
  };
  coupon_info?: {
    code: string;
    discount_amount: number;
  };
}

/**
 * Generates consistent metadata object for payment gateway integration
 */
export const generatePaymentMetadata = ({
  storeId,
  paymentType,
  cartItems,
  amounts,
  customerInfo,
  subscriptionInfo,
  domainInfo,
  couponInfo
}: {
  storeId: string;
  paymentType: PaymentMetadata['payment_type'];
  cartItems?: Array<{
    id: string | number;
    quantity: number;
    selectedVariations?: any[];
  }>;
  amounts?: {
    orderTotal: number;
    shippingTotal: number;
    vatAmount: number;
    total: number;
  };
  customerInfo?: {
    email: string;
    name: string;
  };
  subscriptionInfo?: {
    period: number;
    type: 'monthly' | 'yearly';
    is_special_site: boolean;
  };
  domainInfo?: {
    domain: string;
    original_email: string;
  };
  couponInfo?: {
    code: string;
    discount_amount: number;
  };
}): PaymentMetadata => {
  const metadata: PaymentMetadata = {
    store_id: storeId,
    payment_type: paymentType,
    timestamp: new Date().getTime()
  };

  // Add order items if provided
  if (cartItems?.length) {
    metadata.order_items = cartItems.map(item => ({
      id: item.id,
      quantity: item.quantity,
      variations: item.selectedVariations || []
    }));
    metadata.transaction_type = 'order';
  }

  // Add amounts if provided
  if (amounts) {
    metadata.amounts = {
      order: amounts.orderTotal,
      shipping: amounts.shippingTotal,
      vat: amounts.vatAmount,
      total: amounts.total
    };
  }

  // Add customer info if provided  
  if (customerInfo) {
    metadata.customer_info = customerInfo;
    metadata.custom_fields = [
      {
        display_name: "Store ID",
        variable_name: "store_id",
        value: storeId
      },
      {
        display_name: "Customer Name",
        variable_name: "customer_name",
        value: customerInfo.name
      }
    ];
  }

  // Add subscription info if provided
  if (subscriptionInfo) {
    metadata.transaction_type = 'subscription';
    metadata.subscription_info = subscriptionInfo;
  }

  // Add domain info if provided
  if (domainInfo) {
    metadata.transaction_type = 'domain';
    metadata.domain_info = domainInfo;
  }

  // Add coupon info if provided
  if (couponInfo) {
    metadata.coupon_info = couponInfo;
  }

  return metadata;
}
